import IconButton from '@/global/components/Button/IconButton'
import GridCheckboxMenuButton from '@/global/components/DataGrid/components/CustomGridToolBar/GridCheckboxMenuButton'
import ImprimirButton from '@/global/components/DataGrid/components/CustomGridToolBar/ImprimirButton'
import { GridButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { useFilterLocaisCentroStore } from '@/global/components/Selects/filter-select'
import { hoursToMinutes, minutesToHours } from '@acme/common'
import SP, { usePeriodo } from 'global/components/Selects/SelectPeriodo'
import ContentGuide from 'global/guides/ContentGuide'
import { LibraryBigIcon, ListIcon, SettingsIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { DescontoDSRItem, DescontoDSREmpresas, descontoDsr } from 'service/api'
import { useStore } from 'store'
import { match } from 'ts-pattern'
import { adaptadorDsrCliente } from './adaptador-dsr'
import { colunasDsrCliente } from './colunas-dinamicas'
import { explodirLinhas } from './explodir-linhas'
import {
    LABEL_NIVEL,
    NivelDSR,
    ORDEM_NIVEIS_CLIENTE,
} from './niveis-agrupamento'

export default function RelatorioDescontoDSRCliente() {
    const [isImprimindo, setIsImprimindo] = useState(false)
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const [niveis, setNiveis] = useState<Set<NivelDSR>>(new Set())
    const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
    const empresa = useStore(state => state.company)

    const columns = useMemo(
        () => colunasDsrCliente(niveis, empresa?.dsrExibeHoras),
        [niveis, empresa?.dsrExibeHoras],
    )
    const [sortModel, setSortModel, sort] = useDefaultSortModel(columns)

    const { data = [], isLoading } = descontoDsr.useFetch({
        queryParams: {
            dataInicial,
            dataFinal,
            empresaId: empresa?.id,
        },
        enabled: !!empresa,
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = useMemo(() => {
        const filtrados = (data as DescontoDSRItem[]).filter(useFilter)
        return explodirLinhas(filtrados, niveis)
    }, [data, useFilter, niveis])

    const groupingModel = useMemo(() => {
        return adaptadorDsrCliente.obterGroupingModel(niveis)
    }, [niveis])

    const handleClick = async () => {
        setIsImprimindo(true)
        const niveisParam = ORDEM_NIVEIS_CLIENTE.filter(n =>
            niveis.has(n),
        ).join(',')
        const filteredRows = rows?.filter(
            new FilterModelPro(filterModel, columns).filter,
        )
        const sortedIds = sort(filteredRows).map(({ id }) => id)

        descontoDsr
            .pdf({
                data: sortedIds,
                queryParam: {
                    dataInicial,
                    dataFinal,
                    empresaId: empresa?.id,
                    niveis: niveisParam || undefined,
                },
            })
            .finally(() => {
                setIsImprimindo(false)
            })
    }

    const MemoizedFooter = React.useMemo(() => {
        if (!empresa) return () => <></>

        const filteredData =
            rows?.filter(new FilterModelPro(filterModel, columns).filter) ?? []

        const total = match(empresa.dsrExibeHoras)
            .with('S', () => {
                return minutesToHours(
                    filteredData.reduce(
                        (acc, curr) => acc + hoursToMinutes(curr.valor),
                        0,
                    ),
                )
            })
            .otherwise(() => {
                return (
                    (filteredData.reduce(
                        (acc, curr) =>
                            acc + Number(String(curr.valor).replace(/\D/g, '')),
                        0,
                    ) || 0) + ' dia(s)'
                )
            })

        return () => (
            <div style={{ flex: 1, padding: '1rem' }}>
                <span>Total descontado: {total}</span>
            </div>
        )
    }, [empresa?.dsrExibeHoras, rows, filterModel, columns])

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header label={['Relatórios', 'Desconto de DSR']}>
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SP.Root
                            onChange={setPeriodo}
                            value={[dataInicial, dataFinal]}
                            allowedTypes={['competencia', 'periodo']}
                        >
                            <SP.DateSelector />
                        </SP.Root>
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    loading={isLoading}
                    columns={columns}
                    rows={rows}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    enableRowGrouping={groupingModel.length > 0}
                    groupingModel={groupingModel}
                    enableRowExpanding={groupingModel.length > 0}
                    getRowCanExpand={row =>
                        adaptadorDsrCliente.podeExpandirLinha(
                            row,
                            groupingModel.length,
                        )
                    }
                    components={{
                        Toolbar: () => (
                            <>
                                <GridButton
                                    href={`/cadastros/empresas/${empresa?.id}/editar?aba=dsr`}
                                    label="Configurar"
                                    startIcon={<SettingsIcon />}
                                />
                                <GridCheckboxMenuButton
                                    key="niveis"
                                    startIcon={<LibraryBigIcon />}
                                    label="Agrupar"
                                    options={ORDEM_NIVEIS_CLIENTE.map(n => ({
                                        value: n,
                                        label: LABEL_NIVEL[n],
                                    }))}
                                    selected={niveis}
                                    onChange={(selected: any) =>
                                        setNiveis(selected as Set<NivelDSR>)
                                    }
                                />
                                <div style={{ flex: 1 }} />
                                <ImprimirButton
                                    handleClick={handleClick}
                                    isImprimindo={isImprimindo}
                                />
                            </>
                        ),
                        Footer: MemoizedFooter,
                    }}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: 'exportacao-dsr-cliente',
                        },
                        showVisibilityButton: true,
                    }}
                />
            </ContentGuide.Card>
            <Outlet context={{ data: rows }} />
        </ContentGuide>
    )
}

export function useRelatorioDescontoDsrOutletContext() {
    return useOutletContext<{ data?: any[] }>()
}
